import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2, Plus, Upload, FileText, PlayCircle, GraduationCap, ChevronLeft, Save } from "lucide-react";
import { toast } from "sonner";

function uid(prefix = "id") {
  return `${prefix}_${Math.random().toString(36).slice(2, 8)}`;
}

function LessonForm({ courseId, onCreated }) {
  const fileRef = useRef();
  const [form, setForm] = useState({
    title: "", description: "", video_type: "url", video_url: "", duration_min: 0, order: 0,
  });
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, video_url: data.path, video_type: "upload" }));
      toast.success("Video subido");
    } catch {
      toast.error("Error al subir el video");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.title || !form.video_url) {
      toast.error("Título y video son requeridos");
      return;
    }
    try {
      await api.post(`/courses/${courseId}/lessons`, {
        ...form,
        duration_min: Number(form.duration_min) || 0,
        order: Number(form.order) || 0,
      });
      toast.success("Lección agregada");
      setForm({ title: "", description: "", video_type: "url", video_url: "", duration_min: 0, order: 0 });
      if (fileRef.current) fileRef.current.value = "";
      onCreated?.();
    } catch {
      toast.error("Error al crear lección");
    }
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-md p-5 space-y-3">
      <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Nueva lección de video</p>
      <Input placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="lesson-title-input" />
      <Textarea placeholder="Descripción (opcional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      <div className="flex gap-2 items-center">
        <select
          value={form.video_type}
          onChange={(e) => setForm({ ...form, video_type: e.target.value, video_url: "" })}
          className="border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
          data-testid="lesson-video-type-select"
        >
          <option value="url">Enlace externo (YouTube, Vimeo)</option>
          <option value="upload">Subir archivo</option>
        </select>
      </div>
      {form.video_type === "url" ? (
        <Input placeholder="URL del video" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} data-testid="lesson-video-url-input" />
      ) : (
        <div className="flex items-center gap-3">
          <input ref={fileRef} type="file" accept="video/*" onChange={handleUpload} className="text-sm" data-testid="lesson-video-file-input" />
          {uploading && <span className="text-xs text-[#737373]">Subiendo...</span>}
          {form.video_url && !uploading && <span className="text-xs text-[#1B2E7A]">Listo</span>}
        </div>
      )}
      <div className="flex gap-2">
        <Input type="number" placeholder="Duración (min)" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="max-w-[180px]" />
        <Input type="number" placeholder="Orden" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="max-w-[120px]" />
      </div>
      <Button onClick={submit} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="add-lesson-button">
        <Plus className="h-4 w-4 mr-2" /> Agregar lección
      </Button>
    </div>
  );
}

function PdfForm({ courseId, onCreated }) {
  const fileRef = useRef();
  const [form, setForm] = useState({ title: "", pdf_type: "upload", pdf_url: "", order: 0 });
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, pdf_url: data.path, pdf_type: "upload" }));
      toast.success("PDF subido");
    } catch {
      toast.error("Error al subir el PDF");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.title || !form.pdf_url) {
      toast.error("Título y PDF son requeridos");
      return;
    }
    try {
      await api.post(`/courses/${courseId}/pdfs`, {
        ...form,
        order: Number(form.order) || 0,
      });
      toast.success("PDF agregado");
      setForm({ title: "", pdf_type: "upload", pdf_url: "", order: 0 });
      if (fileRef.current) fileRef.current.value = "";
      onCreated?.();
    } catch {
      toast.error("Error al agregar PDF");
    }
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-md p-5 space-y-3">
      <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Nuevo material PDF</p>
      <Input placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="pdf-title-input" />
      <div className="flex gap-2 items-center">
        <select
          value={form.pdf_type}
          onChange={(e) => setForm({ ...form, pdf_type: e.target.value, pdf_url: "" })}
          className="border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
          data-testid="pdf-type-select"
        >
          <option value="upload">Subir archivo</option>
          <option value="url">Enlace externo</option>
        </select>
      </div>
      {form.pdf_type === "url" ? (
        <Input placeholder="URL del PDF" value={form.pdf_url} onChange={(e) => setForm({ ...form, pdf_url: e.target.value })} data-testid="pdf-url-input" />
      ) : (
        <div className="flex items-center gap-3">
          <input ref={fileRef} type="file" accept="application/pdf" onChange={handleUpload} className="text-sm" data-testid="pdf-file-input" />
          {uploading && <span className="text-xs text-[#737373]">Subiendo...</span>}
          {form.pdf_url && !uploading && <span className="text-xs text-[#1B2E7A]">Listo</span>}
        </div>
      )}
      <Input type="number" placeholder="Orden" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="max-w-[120px]" />
      <Button onClick={submit} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="add-pdf-button">
        <Plus className="h-4 w-4 mr-2" /> Agregar PDF
      </Button>
    </div>
  );
}

function QuizEditor({ courseId, initialQuiz, onSaved }) {
  const [title, setTitle] = useState(initialQuiz?.title || "Examen final");
  const [passScore, setPassScore] = useState(initialQuiz?.pass_score || 70);
  const [questions, setQuestions] = useState(
    initialQuiz?.questions || []
  );

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        id: uid("q"),
        text: "",
        options: [
          { id: uid("o"), text: "" },
          { id: uid("o"), text: "" },
          { id: uid("o"), text: "" },
          { id: uid("o"), text: "" },
        ],
        correct_option_id: "",
      },
    ]);
  };

  const updateQ = (i, patch) => {
    const next = [...questions];
    next[i] = { ...next[i], ...patch };
    setQuestions(next);
  };

  const updateOption = (qi, oi, text) => {
    const next = [...questions];
    next[qi].options[oi] = { ...next[qi].options[oi], text };
    setQuestions(next);
  };

  const removeQuestion = (i) => {
    const next = [...questions];
    next.splice(i, 1);
    setQuestions(next);
  };

  const save = async () => {
    if (questions.length === 0) {
      toast.error("Agrega al menos una pregunta");
      return;
    }
    for (const q of questions) {
      if (!q.text || !q.correct_option_id) {
        toast.error("Cada pregunta debe tener texto y respuesta correcta marcada");
        return;
      }
      for (const o of q.options) {
        if (!o.text) {
          toast.error("Todas las opciones deben tener texto");
          return;
        }
      }
    }
    try {
      await api.post(`/courses/${courseId}/quiz`, {
        title,
        pass_score: Number(passScore) || 70,
        questions,
      });
      toast.success("Examen guardado");
      onSaved?.();
    } catch {
      toast.error("Error al guardar examen");
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#E5E7EB] rounded-md p-5 space-y-3">
        <Input placeholder="Título del examen" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="quiz-title-input" />
        <div className="flex items-center gap-3">
          <label className="text-sm text-[#374151]">Calificación mínima para aprobar</label>
          <Input type="number" value={passScore} onChange={(e) => setPassScore(e.target.value)} className="max-w-[120px]" data-testid="quiz-pass-score-input" />
          <span className="text-sm text-[#737373]">%</span>
        </div>
      </div>

      {questions.map((q, qi) => (
        <div key={q.id} className="bg-white border border-[#E5E7EB] rounded-md p-5" data-testid={`quiz-question-${qi}`}>
          <div className="flex items-start justify-between mb-3">
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Pregunta {qi + 1}</p>
            <button onClick={() => removeQuestion(qi)} className="text-[#DC2626] text-xs hover:underline inline-flex items-center gap-1">
              <Trash2 className="h-3.5 w-3.5" /> Eliminar
            </button>
          </div>
          <Textarea
            placeholder="Enunciado de la pregunta"
            value={q.text}
            onChange={(e) => updateQ(qi, { text: e.target.value })}
            data-testid={`question-text-${qi}`}
          />
          <div className="mt-3 space-y-2">
            {q.options.map((o, oi) => (
              <div key={o.id} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`correct-${q.id}`}
                  checked={q.correct_option_id === o.id}
                  onChange={() => updateQ(qi, { correct_option_id: o.id })}
                  className="accent-[#1B2E7A]"
                  data-testid={`question-${qi}-correct-${oi}`}
                />
                <Input
                  placeholder={`Opción ${oi + 1}`}
                  value={o.text}
                  onChange={(e) => updateOption(qi, oi, e.target.value)}
                  data-testid={`question-${qi}-option-${oi}`}
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-[#737373] mt-3">
            Marca la opción correcta seleccionando el círculo a la izquierda.
          </p>
        </div>
      ))}

      <div className="flex gap-3">
        <Button onClick={addQuestion} variant="outline" className="border-[#E5E7EB]" data-testid="add-question-button">
          <Plus className="h-4 w-4 mr-2" /> Añadir pregunta
        </Button>
        <Button onClick={save} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="save-quiz-button">
          <Save className="h-4 w-4 mr-2" /> Guardar examen
        </Button>
      </div>
    </div>
  );
}

export default function AdminCourseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editForm, setEditForm] = useState(null);
  const [allBooks, setAllBooks] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, lib] = await Promise.all([
        api.get(`/courses/${id}`),
        api.get("/library"),
      ]);
      setData(r.data);
      setAllBooks(lib.data);
      setEditForm({
        title: r.data.course.title,
        description: r.data.course.description,
        topic: r.data.course.topic,
        cover_url: r.data.course.cover_url || "",
        order: r.data.course.order || 0,
        book_ids: r.data.course.book_ids || [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const saveCourse = async () => {
    try {
      await api.patch(`/courses/${id}`, {
        ...editForm,
        order: Number(editForm.order) || 0,
      });
      toast.success("Curso actualizado");
      load();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const removeLesson = async (lid) => {
    if (!window.confirm("¿Eliminar lección?")) return;
    await api.delete(`/lessons/${lid}`);
    toast.success("Lección eliminada");
    load();
  };
  const removePdf = async (pid) => {
    if (!window.confirm("¿Eliminar PDF?")) return;
    await api.delete(`/pdfs/${pid}`);
    toast.success("PDF eliminado");
    load();
  };

  if (loading || !data) return <div className="text-[#374151]">Cargando...</div>;

  return (
    <div data-testid="admin-course-editor-page">
      <button
        onClick={() => navigate("/admin")}
        className="inline-flex items-center gap-1 text-sm text-[#374151] hover:text-[#1A1A1A] mb-6"
        data-testid="back-to-admin"
      >
        <ChevronLeft className="h-4 w-4" /> Volver al panel
      </button>

      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2">Editor de curso</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-[#1A1A1A]">{data.course.title}</h1>
      </div>

      <Tabs defaultValue="info">
        <TabsList className="bg-[#F3F4F6] border border-[#E5E7EB]">
          <TabsTrigger value="info" className="data-[state=active]:bg-white">Información</TabsTrigger>
          <TabsTrigger value="lessons" className="data-[state=active]:bg-white">
            <PlayCircle className="h-4 w-4 mr-2" /> Lecciones
          </TabsTrigger>
          <TabsTrigger value="pdfs" className="data-[state=active]:bg-white">
            <FileText className="h-4 w-4 mr-2" /> PDFs
          </TabsTrigger>
          <TabsTrigger value="quiz" className="data-[state=active]:bg-white">
            <GraduationCap className="h-4 w-4 mr-2" /> Examen
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-6">
          <div className="bg-white border border-[#E5E7EB] rounded-md p-6 space-y-3 max-w-2xl">
            <Input placeholder="Título" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
            <Input placeholder="Tema" value={editForm.topic} onChange={(e) => setEditForm({ ...editForm, topic: e.target.value })} />
            <Textarea placeholder="Descripción" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
            <Input placeholder="URL portada" value={editForm.cover_url} onChange={(e) => setEditForm({ ...editForm, cover_url: e.target.value })} />
            <Input type="number" placeholder="Orden" value={editForm.order} onChange={(e) => setEditForm({ ...editForm, order: e.target.value })} />

            <div className="pt-2">
              <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280] mb-2">Libros relacionados</p>
              <div className="border border-[#E5E7EB] rounded-md p-2 space-y-1 max-h-56 overflow-auto">
                {allBooks.length === 0 && <p className="text-sm text-[#737373] p-2">Aún no hay libros en la biblioteca.</p>}
                {allBooks.map((b) => {
                  const checked = (editForm.book_ids || []).includes(b.id);
                  return (
                    <label key={b.id} className="flex items-center gap-2 text-sm p-1.5 hover:bg-[#FFFFFF] rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const cur = new Set(editForm.book_ids || []);
                          if (checked) cur.delete(b.id); else cur.add(b.id);
                          setEditForm({ ...editForm, book_ids: Array.from(cur) });
                        }}
                        className="accent-[#1B2E7A]"
                        data-testid={`course-book-${b.id}`}
                      />
                      <span className="flex-1">{b.title}</span>
                      <span className="text-xs text-[#6B7280]">{b.category}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-[#737373] mt-2">
                Los libros seleccionados aparecerán como referencias en la pestaña "Libros" del curso.
              </p>
            </div>

            <Button onClick={saveCourse} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="save-course-info">
              <Save className="h-4 w-4 mr-2" /> Guardar cambios
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="lessons" className="mt-6 space-y-4">
          <div className="grid lg:grid-cols-2 gap-6">
            <LessonForm courseId={id} onCreated={load} />
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2">Lecciones existentes</p>
              {data.lessons.length === 0 && <p className="text-sm text-[#737373]">Sin lecciones aún.</p>}
              {data.lessons.map((l) => (
                <div key={l.id} className="bg-white border border-[#E5E7EB] rounded-md p-4 flex items-center justify-between" data-testid={`existing-lesson-${l.id}`}>
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A]">{l.title}</p>
                    <p className="text-xs text-[#737373]">{l.video_type === "url" ? "Enlace externo" : "Archivo"}</p>
                  </div>
                  <button onClick={() => removeLesson(l.id)} className="text-[#DC2626] hover:bg-[#DC2626]/10 p-2 rounded-md">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pdfs" className="mt-6 space-y-4">
          <div className="grid lg:grid-cols-2 gap-6">
            <PdfForm courseId={id} onCreated={load} />
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2">PDFs existentes</p>
              {data.pdfs.length === 0 && <p className="text-sm text-[#737373]">Sin PDFs aún.</p>}
              {data.pdfs.map((p) => (
                <div key={p.id} className="bg-white border border-[#E5E7EB] rounded-md p-4 flex items-center justify-between" data-testid={`existing-pdf-${p.id}`}>
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A]">{p.title}</p>
                    <p className="text-xs text-[#737373]">{p.pdf_type === "url" ? "Enlace externo" : "Archivo"}</p>
                  </div>
                  <button onClick={() => removePdf(p.id)} className="text-[#DC2626] hover:bg-[#DC2626]/10 p-2 rounded-md">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="quiz" className="mt-6">
          <QuizEditor courseId={id} initialQuiz={data.quiz} onSaved={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
